import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";

export type LocalPCAdminExceptionFormValues = {
  employeeName: string;
  position?: string;
  employeeID: string;
  department?: string;
  location?: string;
  reason: string;
  actionType?: string;
  device?: string;
  requestType?: string;
  permission?: string;
  accessDuration?: string;
  machineNames?: string;
  computerName?: string;
  computerModel?: string;
};

type Key = keyof LocalPCAdminExceptionFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: LocalPCAdminExceptionFormValues) => void;
  open: boolean;
  values: Partial<LocalPCAdminExceptionFormValues>;
  readonly?: boolean;
};

export const LocalPCAdminExceptionForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<LocalPCAdminExceptionFormValues>({
    employeeName: "",
    position: undefined,
    employeeID: "",
    department: undefined,
    location: undefined,
    reason: "",
    actionType: undefined,
    device: undefined,
    requestType: undefined,
    permission: undefined,
    accessDuration: undefined,
    machineNames: undefined,
    computerName: undefined,
    computerModel: undefined,
    ...values,
  });

  function onFormValueChange(
    name: keyof LocalPCAdminExceptionFormValues,
    value: any
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "employeeName":
      case "reason":
      case "computerName":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);

        if (valid) {
          onSubmit(form.values);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="Local PC Admin Exception Authorization Form"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1 - Information</Form.Title>
        <Form.Textbox
          error={form.errors.employeeName}
          label="Employee Name"
          name="employeeName"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.employeeName}
        />
        <Form.Textbox
          label="Department Name"
          name="department"
          onChange={onFormValueChange}
          size={6}
          value={form.values.department}
        />
        <Form.Textbox
          label="Position"
          name="position"
          onChange={onFormValueChange}
          size={6}
          value={form.values.position}
        />
        <Form.Textbox
          label="Location / Office / Cube#"
          name="location"
          onChange={onFormValueChange}
          size={6}
          value={form.values.location}
        />
        <Form.Textbox
          error={form.errors.employeeID}
          label="Employee ID"
          name="employeeID"
          onChange={onFormValueChange}
          size={6}
          value={form.values.employeeID}
        />
        <Form.Title subtitle="Please state the full business reason for the request">
          2 - Request Reason
        </Form.Title>
        <Form.Textarea
          error={form.errors.reason}
          label="Justification"
          name="reason"
          onChange={onFormValueChange}
          placeholder="Write details here"
          required
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.reason}
        />
        <Form.Title>3 - Request Information</Form.Title>
        <Form.Dropdown
          label="Type of Action"
          onChange={onFormValueChange}
          options={["New Access", ""]}
          name="actionType"
          size={6}
          value={form.values.actionType || ""}
        />
        <Form.Dropdown
          label="Request Type"
          onChange={onFormValueChange}
          options={["Add", "Removed"]}
          name="requestType"
          size={6}
          value={form.values.requestType || ""}
        />
        <Form.Dropdown
          label="Device"
          onChange={onFormValueChange}
          options={["PC Local Admin", ""]}
          name="device"
          size={6}
          value={form.values.device || ""}
        />
        <Form.Dropdown
          label="Permission"
          onChange={onFormValueChange}
          options={["Permanent", "Temporary"]}
          name="permission"
          size={6}
          value={form.values.permission || ""}
        />
        <Form.Textbox
          label="Access Duration:"
          name="accessDuration"
          onChange={onFormValueChange}
          size={6}
          value={form.values.accessDuration}
        />
        <Form.Title>4 - Device Information</Form.Title>
        <Form.Textbox
          label="Machine Names - (on which device(s) will be used) -"
          name="machineNames"
          onChange={onFormValueChange}
          size={6}
          value={form.values.machineNames}
        />
        <Form.Textbox
          label="Computer Model"
          name="computerModel"
          onChange={onFormValueChange}
          size={6}
          value={form.values.computerModel}
        />
        <Form.Textbox
          error={form.errors.computerName}
          label="Computer Name"
          name="computerName"
          onChange={onFormValueChange}
          size={6}
          required
          value={form.values.computerName}
        />
      </Form>
    </Wrapper>
  );
};
